<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import Map from "ol/Map.js";
import View from "ol/View.js";
import VectorTileLayer from "ol/layer/VectorTile.js";
import VectorSource from "ol/source/Vector.js";
import Cluster from "ol/source/Cluster.js";
import VectorLayer from "ol/layer/Vector.js";
import Feature from "ol/Feature.js";
import Point from "ol/geom/Point.js";
import { fromLonLat } from "ol/proj.js";
import { boundingExtent } from "ol/extent.js";
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from "ol/style.js";
import Overlay from "ol/Overlay.js";
import Attribution from "ol/control/Attribution.js";
import Zoom from "ol/control/Zoom.js";
import { applyBackground, applyStyle } from "ol-mapbox-style";
import IpGeoPopupCard from "./IpGeoPopupCard.vue";
import IpGeoClusterCard from "./IpGeoClusterCard.vue";

const props = defineProps({
    items: {
        type: Array,
        required: true,
    },
});

const mapEl = ref(null);
const popupEl = ref(null);
const selectedItem = ref(null);
const selectedCluster = ref(null);

let map = null;
let popup = null;
let markerSource = null;
let clusterSource = null;

const clusterStyleCache = {};

// OpenFreeMap serves MapLibre vector styles with no API key; ol-mapbox-style translates
// them into OpenLayers layers, so clustering and overlays below stay untouched.
const basemapStyleUrl = "https://tiles.openfreemap.org/styles/bright";

function createBasemapLayer() {
    const layer = new VectorTileLayer({ declutter: true });

    // The style declares two sources (shaded relief and vector), so the vector one has to
    // be named explicitly. Attribution travels with the source TileJSON.
    Promise.all([
        applyStyle(layer, basemapStyleUrl, "openmaptiles"),
        applyBackground(layer, basemapStyleUrl),
    ]).catch((error) => console.error("Failed to load basemap style.", error));

    return layer;
}

function createMarkerStyle(label) {
    return new Style({
        image: new CircleStyle({
            radius: 8,
            fill: new Fill({ color: "#1565c0" }),
            stroke: new Stroke({ color: "#fff", width: 2 }),
        }),
        text: new Text({
            text: label,
            offsetY: -20,
            font: "bold 12px sans-serif",
            fill: new Fill({ color: "#1a1a1a" }),
            stroke: new Stroke({ color: "#fff", width: 3 }),
        }),
    });
}

function createClusterStyle(size) {
    if (clusterStyleCache[size]) {
        return clusterStyleCache[size];
    }

    const radius = 12 + Math.min(size, 100) / 8;

    const style = new Style({
        image: new CircleStyle({
            radius,
            fill: new Fill({ color: "#1565c0" }),
            stroke: new Stroke({ color: "#fff", width: 2 }),
        }),
        text: new Text({
            text: String(size),
            font: "bold 13px sans-serif",
            fill: new Fill({ color: "#fff" }),
        }),
    });

    clusterStyleCache[size] = style;

    return style;
}

// Cluster source emits one feature per cluster; its `features` property holds the originals.
// A single-member cluster renders as a labelled marker, a multi-member one as a counted bubble.
function clusterStyle(feature) {
    const features = feature.get("features");
    const size = features.length;

    if (size > 1) {
        return createClusterStyle(size);
    }

    const item = features[0].get("item");

    return createMarkerStyle(item.label || item.ip);
}

function renderMarkers(items) {
    if (markerSource === null) {
        return;
    }

    markerSource.clear();

    items.forEach((item) => {
        if (!item.geo?.latitude || !item.geo?.longitude) {
            return;
        }

        const feature = new Feature({
            geometry: new Point(
                fromLonLat([
                    Number(item.geo.longitude),
                    Number(item.geo.latitude),
                ]),
            ),
            item,
        });

        markerSource.addFeature(feature);
    });
}

function closePopup() {
    selectedItem.value = null;
    selectedCluster.value = null;
    popup?.setPosition(undefined);
}

function handleClick(event) {
    const feature = map.forEachFeatureAtPixel(
        event.pixel,
        (currentFeature) => currentFeature,
    );

    const features = feature?.get("features");

    if (!features) {
        closePopup();
        return;
    }

    if (features.length > 1) {
        const coordinates = features.map((member) =>
            member.getGeometry().getCoordinates(),
        );
        const [originX, originY] = coordinates[0];
        const separable = coordinates.some(
            ([x, y]) => x !== originX || y !== originY,
        );

        // Members spread across the map: zoom into their extent to break the cluster apart.
        if (separable) {
            closePopup();

            map.getView().fit(boundingExtent(coordinates), {
                duration: 400,
                padding: [80, 80, 80, 80],
                maxZoom: 12,
            });

            return;
        }

        // Identical coordinates (e.g. city-level geo): zoom cannot separate them, so list
        // every IP at this spot in a detail card instead.
        selectedItem.value = null;
        selectedCluster.value = features.map((member) => member.get("item"));
        popup.setPosition(feature.getGeometry().getCoordinates());

        return;
    }

    const member = features[0];
    selectedCluster.value = null;
    selectedItem.value = member.get("item");
    popup.setPosition(member.getGeometry().getCoordinates());
}

function handlePointerMove(event) {
    map.getTargetElement().style.cursor = map.hasFeatureAtPixel(event.pixel)
        ? "pointer"
        : "";
}

onMounted(() => {
    markerSource = new VectorSource();
    clusterSource = new Cluster({
        distance: 40,
        minDistance: 20,
        source: markerSource,
    });

    popup = new Overlay({
        element: popupEl.value,
        positioning: "bottom-center",
        stopEvent: true,
        offset: [0, -14],
        autoPan: true,
    });

    map = new Map({
        target: mapEl.value,
        controls: [
            new Zoom(),
            new Attribution({ collapsible: true, collapsed: true }),
        ],
        layers: [
            createBasemapLayer(),
            new VectorLayer({
                source: clusterSource,
                style: clusterStyle,
            }),
        ],
        overlays: [popup],
        view: new View({
            center: fromLonLat([20, 20]),
            zoom: 2,
        }),
    });

    map.on("click", handleClick);
    map.on("pointermove", handlePointerMove);

    renderMarkers(props.items);
});

watch(
    () => props.items,
    (items) => renderMarkers(items),
    { deep: true },
);

onBeforeUnmount(() => {
    if (map !== null) {
        map.un("click", handleClick);
        map.un("pointermove", handlePointerMove);
        map.setTarget(undefined);
    }
});
</script>

<template>
    <div class="ip-geo-map mb-[22px]">
        <div ref="mapEl" class="ip-geo-map__canvas"></div>

        <div ref="popupEl" class="ip-geo-map__popup">
            <IpGeoClusterCard
                v-if="selectedCluster"
                :items="selectedCluster"
                @close="closePopup"
            />
            <IpGeoPopupCard
                v-else-if="selectedItem"
                :item="selectedItem"
                @close="closePopup"
            />
        </div>
    </div>
</template>

<style scoped>
.ip-geo-map {
    position: relative;
    width: 100%;
}

.ip-geo-map__canvas {
    width: 100%;
    height: 640px;
    border-radius: 16px;
    overflow: hidden;
}

.ip-geo-map__popup {
    min-width: 260px;
}
</style>
